import React, { Component, ErrorInfo, ReactNode } from 'react';

type Props = { children: ReactNode };

type State = { hasError: boolean; message: string; stackHint: string | null };

/** Fallback sem MUI: se o problema envolver tema/portais, a UI deve continuar acessível */
function CrashFallback(props: {
  message: string;
  stackHint: string | null;
  onReload: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: 'linear-gradient(165deg, #f0fdfa 0%, #f8fafc 50%, #eff6ff 100%)',
        color: '#0f172a',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          backgroundColor: '#fff',
          borderRadius: 16,
          padding: '28px 24px',
          boxShadow: '0 24px 48px rgba(15,23,42,0.10)',
          border: '1px solid rgba(226,232,240,0.95)',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', margin: '0 0 12px', fontWeight: 700 }}>
          Ocorreu um erro na interface
        </h1>
        <p style={{ margin: '0 0 16px', lineHeight: 1.55, fontSize: '0.9375rem', color: '#334155' }}>
          Às vezes a página pode ficar aparentemente em branco após uma falha de rede, do servidor ou de
          atualização da aplicação. Recarregar costuma recuperar.
        </p>
        <p style={{ margin: '0 0 16px', lineHeight: 1.5, fontSize: '0.875rem', color: '#475569' }}>
          <strong>Sugestões:</strong>
        </p>
        <ul
          style={{
            margin: '0 0 20px',
            paddingLeft: 20,
            lineHeight: 1.65,
            fontSize: '0.875rem',
            color: '#475569',
          }}
        >
          <li>
            Clique em <strong>Recarregar a página</strong> abaixo.
          </li>
          <li>
            Abra os detalhes técnicos (F12 → Consola): erros como <strong>503</strong> indicam servidor
            indisponível; <strong>removeChild</strong> ou semelhantes podem estar ligados a cache antigo —
            experimente atualizar com ctrl+F5 ou janela anónima para descartar extensões do browser.
          </li>
          <li>Se todas as estações ficarem igual, envolva quem gere a infraestrutura (servidor/CDN).</li>
        </ul>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button
            type="button"
            onClick={props.onReload}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.9375rem',
              background: 'linear-gradient(145deg, #0f766e 0%, #14b8a6 100%)',
              color: '#fff',
              boxShadow: '0 4px 14px rgba(15,118,110,0.35)',
            }}
          >
            Recarregar a página
          </button>
          <a
            href={window.location.origin + '/login'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '10px 16px',
              fontSize: '0.875rem',
              color: '#0d9488',
              fontWeight: 600,
            }}
          >
            Ir ao login
          </a>
        </div>
        {props.stackHint ? (
          <details style={{ marginTop: 22, fontSize: '0.75rem', color: '#64748b' }}>
            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
              Detalhe técnico (para suporte)
            </summary>
            <pre
              style={{
                marginTop: 10,
                padding: 12,
                overflow: 'auto',
                borderRadius: 8,
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {props.stackHint}
            </pre>
          </details>
        ) : null}
        {props.message ? (
          <p style={{ marginTop: 16, marginBottom: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
            {props.message}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '', stackHint: null };
  }

  static getDerivedStateFromError(err: Error): Partial<State> {
    const msg = err?.message?.trim() || 'Erro desconhecido';
    const stackHint = process.env.NODE_ENV === 'production' ? msg : `${msg}\n\n${err.stack || ''}`;
    return { hasError: true, message: msg, stackHint };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // Manter sempre um registo mesmo com console desactivado na build
    const payload = `[AppErrorBoundary] ${err.message}\n${info.componentStack || ''}`;
    try {
      // eslint-disable-next-line no-console -- diagnostic path for desks and support
      console.error(payload);
    } catch {
      /* ignore */
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <CrashFallback
          message={this.state.message}
          stackHint={this.state.stackHint}
          onReload={this.handleReload}
        />
      );
    }
    return this.props.children;
  }
}
